import { useState } from 'react';

import styles from './TabsDropdown.module.scss';

import { Badge } from '../../display/Badge';
import { LegacyFloatingBox } from '../../../legacy/LegacyFloatingBox/LegacyFloatingBox';
import { Tooltip } from '../../display/Tooltip';
import { Tag } from '../../display/Tag';
import { IconButton } from '../IconButton/IconButton';
import { LegacyMenu } from '../../../legacy/LegacyMenu/LegacyMenu';
import { LegacyMenuItem } from '../../../legacy/LegacyMenu/LegacyMenuItem';
import { useClickOutside } from '../../../hooks';

import type { TabsItemType, TabsSize, OnChooseItem } from './types';

type Props = {
  /**
   * Array of items to render in the dropdown.
   */
  items: Array<TabsItemType>;
  /**
   * Callback for when an item is chosen.
   */
  onChooseItem: OnChooseItem;
  /**
   * Indicates which item is selected based on its href.
   */
  selectedItemHref: string;
  /**
   * The size to render the dropdown at.
   */
  size: TabsSize;
};

export const TabsDropdown = (props: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuItems = props.items.map((item) => (
    <LegacyMenuItem
      disabled={item.disabled}
      href={item.href}
      isSelected={item.href === props.selectedItemHref}
      key={item.href}
      onClick={(event: React.MouseEvent) => {
        setIsOpen(false);
        props.onChooseItem(item, event);
      }}
    >
      {item.text}
      {item.badgeText && (
        <Badge variant={item.badgeVariant} className={styles.badgeAndTag}>
          {item.badgeText}
        </Badge>
      )}
      {item.tagText && (
        <Tag variant={item.tagVariant} className={styles.badgeAndTag}>
          {item.tagText}
        </Tag>
      )}
    </LegacyMenuItem>
  ));
  const [setClickOutsideNode] = useClickOutside({
    onClickOutside: () => {
      setIsOpen(false);
    },
  });
  // Define handlers here to avoid non-applicable linter a11y complaints.
  const containerHandlers = {
    onKeyUp: (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    },
  };
  return (
    <div ref={setClickOutsideNode} {...containerHandlers} data-zds>
      <Tooltip content="More items…" placement="left">
        <IconButton
          variant="tertiary"
          name="navMoreHoriz"
          aria-label="More items…"
          aria-expanded={isOpen}
          onPress={() => setIsOpen(!isOpen)}
          size="small"
        />
      </Tooltip>
      {isOpen && (
        <LegacyFloatingBox align="right">
          <div className={styles.menu} data-zds>
            <LegacyMenu aria-label="">{menuItems}</LegacyMenu>
          </div>
        </LegacyFloatingBox>
      )}
    </div>
  );
};
